import React from 'react';
import { PlusCircleIcon, AlertTriangleIcon, ShieldCheckIcon, CheckSquareIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen15_NewInfo({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const handleApplyUpdates = () => {
    const updatedSteps = data.steps.map((step, i) => {
      // Step 5 (index 4): Lift beam — add wind hazard
      if (i === 4) {
        return {
          ...step,
          hazards: [...step.hazards, {
            hazard: 'Wind affecting load stability',
            controls: ['Stop lift if wind increases or load becomes unstable', 'Confirm radio communication between crane operator and signal person before lift'],
            riskLevel: 'HIGH' as const,
            isNew: true
          }]
        };
      }
      // Step 6 (index 5): Guide beam — add clearance control
      if (i === 5) {
        return {
          ...step,
          hazards: step.hazards.map((h, j) => {
            if (j === 1) {
              // Struck-by swinging load
              return {
                ...h,
                controls: [...h.controls, 'Verify all workers below the lift path are cleared before each lift']
              };
            }
            return h;
          })
        };
      }
      return step;
    });
    updateData({
      steps: updatedSteps,
      selectedHazards: [...data.selectedHazards, 'Wind affecting load stability'],
      engineeringControls: [...data.engineeringControls, 'Stop lift if wind increases or load becomes unstable'],
      adminControls: [...data.adminControls, 'Confirm radio communication between crane operator and signal person before lift'],
      safetyChecks: [...data.safetyChecks, 'Verify all workers below the lift path are cleared before each lift'],
      meetingScore: 72
    });
    onNext();
  };
  return <div className="flex flex-col h-full" data-id="element-531">
      <div className="mb-6" data-id="element-532">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-533">
          New Information Detected
        </h2>
        <p className="text-slate-400" data-id="element-534">
          AI found information from the meeting that should be added to the PTP.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-535">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2" data-id="element-536">
          Detected Additions
        </h3>

        {/* Addition 1 — New hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-red-500" data-id="element-537">
          <div className="flex items-center gap-2 mb-1" data-id="element-538">
            <AlertTriangleIcon className="w-4 h-4 text-red-500" data-id="element-539" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-540">
              Hazard → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-541">
            Wind affecting load stability
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-542">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-543">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-544">
              The foreman said the wind picks up near midday and the load may
              move during lifting.
            </p>
          </div>
        </div>

        {/* Addition 2 — Control for new hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-545">
          <div className="flex items-center gap-2 mb-1" data-id="element-546">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-547" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-548">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-549">
            Stop lift if wind increases or load becomes unstable
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-550">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-551">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-552">
              The crane operator stated the lift must stop immediately if wind
              conditions change or the beam starts rotating.
            </p>
          </div>
        </div>

        {/* Addition 3 — Control for new hazard on Step 5 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-green-500" data-id="element-553">
          <div className="flex items-center gap-2 mb-1" data-id="element-554">
            <ShieldCheckIcon className="w-4 h-4 text-green-500" data-id="element-555" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-556">
              Control → Step 5: Lift beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-557">
            Confirm radio communication between crane operator and signal person
            before lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-558">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-559">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-560">
              The meeting included discussion about hand signals and radio
              backup.
            </p>
          </div>
        </div>

        {/* Addition 4 — Control added to Step 6 */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 border-l-4 border-l-blue-500" data-id="element-561">
          <div className="flex items-center gap-2 mb-1" data-id="element-562">
            <CheckSquareIcon className="w-4 h-4 text-blue-500" data-id="element-563" />
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-564">
              Control → Step 6: Guide beam
            </span>
          </div>
          <p className="text-white font-medium mb-2" data-id="element-565">
            Verify all workers below the lift path are cleared before each lift
          </p>
          <div className="bg-slate-950 rounded p-3" data-id="element-566">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-567">
              Reason
            </p>
            <p className="text-slate-300 text-sm italic" data-id="element-568">
              The foreman reminded the crew to keep the area under the suspended
              load clear.
            </p>
          </div>
        </div>

        {/* Change Summary Box */}
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mt-6 flex items-center justify-center gap-2" data-id="element-569">
          <PlusCircleIcon className="w-5 h-5 text-orange-500" data-id="element-570" />
          <span className="text-orange-400 font-bold" data-id="element-571">
            4 updates will be added to the PTP
          </span>
        </div>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-572">
        <PrimaryButton onClick={handleApplyUpdates} data-id="element-573">
          Apply Updates to PTP
        </PrimaryButton>
        <SecondaryButton onClick={() => {}} data-id="element-574">Dismiss</SecondaryButton>
      </div>
    </div>;
}